/**
 * AppointmentInsuranceDocuments.js
 * Bloco "Documentos do convênio" do painel do agendamento (Fase 2): pedidos
 * médicos e guias da pessoa vinculados a este atendimento. Lista os vínculos
 * com a situação atual de cada documento, desvincula com confirmação e
 * vincula um pedido ou guia ativo da ficha. Só aparece em atendimento por convênio.
 * O "Vincular documento" também cria pedido ou guia (os mesmos formulários da
 * ficha) e já vincula o novo documento; vincular um pedido leva junto as guias
 * ligadas a ele, e vincular uma guia leva junto o pedido dela. Guias de outra
 * operadora (a da carteirinha do atendimento) nem aparecem nem vêm junto; se uma
 * chegar por outro caminho, a linha avisa "Guia de outra operadora" (sem bloquear).
 * Na lista, o pedido sem data diz "Sem vencimento" e a guia com todas as sessões
 * usadas vai para o fim, com o aviso, e continua vinculável.
 * O vencimento é conferido na data do atendimento ("Vencido" mesmo que ainda valha
 * hoje); o pedido vencido tem o lápis para editar/renovar ali mesmo. Ao registrar um
 * pedido novo ou renovar um pelo lápis, oferece usá-lo também nos próximos
 * atendimentos sem pedido válido. Cada linha mostra os anexos do documento e o
 * clipe abre o mesmo popup de anexos da ficha (ver e acrescentar).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import AddLinkOutlinedIcon from '@mui/icons-material/AddLinkOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog';
import MedicalRequestFormPopup from '../../../../patient/areas/insurance/documents/MedicalRequestFormPopup';
import AuthorizationFormPopup from '../../../../patient/areas/insurance/documents/AuthorizationFormPopup';
import { attachmentsLabel, sessionsUsedUp } from '../../../../patient/areas/insurance/documents/documentFormat';
import InsuranceDocumentAttachmentsPopup from '../../../../../components/insurance-documents/InsuranceDocumentAttachmentsPopup';
import {
  MEDICAL_REQUEST_CONTEXT, INSURANCE_AUTHORIZATION_CONTEXT,
} from '../../../../../components/insurance-documents/InsuranceDocumentAttachments';
import styles from './AppointmentInsuranceDocuments.module.css';

const TYPE_REQUEST = 'medical_request';
const TYPE_AUTHORIZATION = 'authorization';

// Situação da guia em rótulo curto, com a cor do chip
const AUTH_STATUS = {
  pending: { label: 'Aguardando', className: 'chipAmber' },
  authorized: { label: 'Autorizada', className: 'chipGreen' },
  denied: { label: 'Negada', className: 'chipRed' },
  cancelled: { label: 'Cancelada', className: 'chipGray' },
};

const typeLabel = (type) => (type === TYPE_REQUEST ? 'Pedido' : 'Guia');

/** 'YYYY-MM-DD' -> 'DD/MM/YYYY' */
function formatDay(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/** Hoje em 'YYYY-MM-DD' (horário local) */
function todayDay() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Vencimento com o termo da ficha: "Vence em" ou "Venceu em" (data já passou hoje).
 * O selo "Vencido" ao lado diz se vale na data do atendimento.
 */
function dueLabel(validUntil) {
  const day = String(validUntil || '').slice(0, 10);
  return `${day < todayDay() ? 'Venceu em' : 'Vence em'} ${formatDay(day)}`;
}

function documentTitle(type, data) {
  if (!data) return typeLabel(type);
  if (type === TYPE_REQUEST) return data.title || 'Pedido médico';
  return data.reference_number ? `Guia ${data.reference_number}` : 'Guia sem número';
}

/** 'YYYY-MM-DD HH:MM:SS' -> 'DD/MM HH:MM' (sem a hora, só 'DD/MM') */
function shortDayTime(value) {
  const day = formatDay(value).slice(0, 5);
  const time = /[ T](\d{2}:\d{2})/.exec(String(value || ''));
  return time ? `${day} ${time[1]}` : day;
}

/** "DD/MM HH:MM" dos atendimentos, no máximo MAX_DAYS e "e mais N" */
const MAX_DAYS = 8;
function shortDays(targets) {
  const days = targets.slice(0, MAX_DAYS).map((t) => shortDayTime(t.date));
  const rest = targets.length - days.length;
  if (rest > 0) return `${days.join(', ')} e mais ${rest}`;
  if (days.length === 1) return days[0];
  return `${days.slice(0, -1).join(', ')} e ${days[days.length - 1]}`;
}

/** Todos os campos atuais do pedido, como a API de update espera (mesmo formato da ficha). */
const requestPayload = (patientId, request) => ({
  id: request.id,
  patientId,
  title: request.title,
  requesting_professional: request.requesting_professional || '',
  issued_at: String(request.issued_at || '').slice(0, 10),
  valid_until: String(request.valid_until || '').slice(0, 10),
  notes: request.notes || '',
});

/**
 * Chips de situação: status da guia, "Vencido/Vencida" e "Inativo" (apagado ou desativado).
 * `expired` já vem conferido na data do atendimento.
 */
function StatusChips({ type, data, expired, missing = false }) {
  if (missing) return <span className={`${styles.chip} ${styles.chipGray}`}>Inativo</span>;
  const status = type === TYPE_AUTHORIZATION ? AUTH_STATUS[data?.status] : null;
  return (
    <>
      {status && <span className={`${styles.chip} ${styles[status.className]}`}>{status.label}</span>}
      {expired && (
        <span className={`${styles.chip} ${styles.chipAmber}`}>{type === TYPE_REQUEST ? 'Vencido' : 'Vencida'}</span>
      )}
      {data && !data.active && <span className={`${styles.chip} ${styles.chipGray}`}>Inativo</span>}
    </>
  );
}

const docKey = (type, id) => `${type}:${id}`;

/**
 * Guia de outra operadora que não a do atendimento (sem operadora conhecida, não filtra).
 * Pedido não tem operadora: sempre serve.
 */
const isOtherProvider = (type, doc, providerId) => type === TYPE_AUTHORIZATION
  && providerId != null && providerId !== '' && doc?.provider_id != null
  && String(doc.provider_id) !== String(providerId);

/**
 * Documentos que entram junto no vínculo: as guias ativas ligadas ao pedido (só as
 * da operadora do atendimento), ou o pedido da guia. Só os ativos e ainda não
 * vinculados a este atendimento.
 */
function pairedDocuments(type, doc, patientDocs, linked, providerId) {
  if (!patientDocs) return [];
  if (type === TYPE_REQUEST) {
    return patientDocs.authorizations
      .filter((g) => g.active && g.status !== 'cancelled' && String(g.medical_request_id) === String(doc.id)
        && !isOtherProvider(TYPE_AUTHORIZATION, g, providerId)
        && !linked.has(docKey(TYPE_AUTHORIZATION, g.id)))
      .map((g) => ({ type: TYPE_AUTHORIZATION, id: g.id }));
  }
  if (!doc.medical_request_id) return [];
  const request = patientDocs.requests.find((r) => String(r.id) === String(doc.medical_request_id));
  return request && request.active && !linked.has(docKey(TYPE_REQUEST, request.id))
    ? [{ type: TYPE_REQUEST, id: request.id }]
    : [];
}

/**
 * @param {Object} props
 * @param {Object} props.appointment - agendamento (id, patient, patient_insurance_id, insurance_provider_id, date)
 * @param {Function} [props.onChanged] - depois de vincular ou desvincular (recarrega os avisos)
 * @param {*} [props.refreshToken] - quando muda, os vínculos são buscados de novo sem piscar
 * @param {*} [props.openPickerToken] - quando muda (e não é vazio), abre o "Vincular documento"
 */
export default function AppointmentInsuranceDocuments({ appointment, onChanged, refreshToken, openPickerToken }) {
  const {
    CompanyConfig, GetAppointmentInsuranceDocuments, GetPatientInsuranceDocuments, GetPatientInsurances,
    LinkAppointmentInsuranceDocument, UnlinkAppointmentInsuranceDocument,
    SetMedicalRequest, UpdateMedicalRequest, SetInsuranceAuthorization,
    GetUpcomingRequestTargets, LinkUpcomingRequest,
  } = DataProviderInstance();
  const providers = CompanyConfig?.InsuranceProviders || [];
  const appointmentId = appointment?.id;
  const patientId = appointment?.patient;
  const cardId = appointment?.patient_insurance_id;
  const providerId = appointment?.insurance_provider_id;
  const appointmentDay = String(appointment?.date || '').slice(0, 10);

  // Vencido na data do atendimento (sem a data, vale o "vencido hoje" da API)
  const isExpiredFor = (data) => {
    if (!data) return false;
    if (!appointmentDay) return Boolean(data.expired);
    return Boolean(data.valid_until) && String(data.valid_until).slice(0, 10) < appointmentDay;
  };

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [patientDocs, setPatientDocs] = useState(null); // null = carregando
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  // Formulários abertos pelo picker: pedido (true) e guia ({ cards })
  const [requestFormOpen, setRequestFormOpen] = useState(false);
  const [authorizationForm, setAuthorizationForm] = useState(null);
  // Pedido vencido aberto para editar/renovar (linha completa da ficha)
  const [editRequest, setEditRequest] = useState(null);
  // Pedido novo que pode valer também nos próximos atendimentos: { requestId, targets }
  const [upcomingOffer, setUpcomingOffer] = useState(null);
  const upcomingRequestId = useRef(null);
  // Anexos abertos pelo clipe da linha: { context, contextId, subject }
  const [attachments, setAttachments] = useState(null);

  // quiet: recarga de fundo, sem o "Carregando..." no lugar da lista
  const loadLinks = useCallback((quiet = false) => {
    if (!appointmentId) return;
    if (!quiet) setLoading(true);
    GetAppointmentInsuranceDocuments(appointmentId, (result) => {
      setLoading(false);
      setLinks(result?.status === 'SUCCESS' && Array.isArray(result.data) ? result.data : []);
    });
  }, [appointmentId, GetAppointmentInsuranceDocuments]);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  // A carga inicial é a de cima; depois, cada troca do token recarrega quieto
  const firstRefresh = useRef(true);
  useEffect(() => {
    if (firstRefresh.current) {
      firstRefresh.current = false;
      return;
    }
    loadLinks(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const loadPatientDocs = () => {
    GetPatientInsuranceDocuments(patientId, (result) => {
      const data = result?.status === 'SUCCESS' ? (result.data || {}) : {};
      setPatientDocs({
        requests: Array.isArray(data.requests) ? data.requests : [],
        authorizations: Array.isArray(data.authorizations) ? data.authorizations : [],
      });
    });
  };

  const openPicker = () => {
    setError(null);
    setPatientDocs(null);
    setPickerOpen(true);
    loadPatientDocs();
  };

  useEffect(() => {
    if (openPickerToken && patientId != null) openPicker();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPickerToken]);

  // Documentos ativos ainda não vinculados a este atendimento (guias só da operadora dele)
  const linked = useMemo(() => new Set(links.map(l => docKey(l.document_type, l.document_id))), [links]);
  const unlinked = useMemo(() => {
    if (!patientDocs) return [];
    const rows = [
      ...patientDocs.authorizations.map(doc => ({ type: TYPE_AUTHORIZATION, doc })),
      ...patientDocs.requests.map(doc => ({ type: TYPE_REQUEST, doc })),
    ];
    return rows.filter(({ type, doc }) => doc.active && !linked.has(docKey(type, doc.id)));
  }, [patientDocs, linked]);
  // Guia com todas as sessões usadas vai para o fim (continua vinculável)
  const available = useMemo(() => {
    const rows = unlinked.filter(({ type, doc }) => !isOtherProvider(type, doc, providerId));
    const usedUp = ({ type, doc }) => type === TYPE_AUTHORIZATION && sessionsUsedUp(doc);
    return [...rows.filter((row) => !usedUp(row)), ...rows.filter(usedUp)];
  }, [unlinked, providerId]);

  const hasAnyPatientDoc = Boolean(patientDocs)
    && (patientDocs.requests.length + patientDocs.authorizations.length) > 0;

  /** Vincula em sequência; para no primeiro erro e devolve quantos entraram e o erro. */
  const linkSequence = (items, done, count = 0) => {
    const [first, ...rest] = items;
    if (!first) {
      done(count, null);
      return;
    }
    LinkAppointmentInsuranceDocument(appointmentId, first.type, first.id, (result) => {
      if (result?.status !== 'SUCCESS') {
        done(count, result?.error || 'Não foi possível vincular. Tente novamente.');
        return;
      }
      linkSequence(rest, done, count + 1);
    });
  };

  // Recarrega os vínculos uma vez e avisa o dono (avisos do convênio)
  const refreshAfterChange = () => {
    loadLinks(true);
    if (onChanged) onChanged();
  };

  /**
   * Vincula o documento e o par dele (pedido e guias); resolve com o erro ou null.
   * `refresh` false: quem chama recarrega depois (formulário que ainda envia anexos).
   */
  const linkWithPairs = (type, doc, refresh = true) => new Promise((resolve) => {
    const items = [{ type, id: doc.id }, ...pairedDocuments(type, doc, patientDocs, linked, providerId)];
    linkSequence(items, (count, failure) => {
      if (count > 0 && refresh) refreshAfterChange();
      resolve(failure);
    });
  });

  const handleLink = (type, doc) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    linkWithPairs(type, doc).then((failure) => {
      setBusy(false);
      if (failure) {
        setError(failure);
        return;
      }
      setPickerOpen(false);
    });
  };

  // Novo pedido / nova guia: o picker fecha e o formulário da ficha abre no lugar dele
  const openRequestForm = () => {
    setPickerOpen(false);
    setRequestFormOpen(true);
  };

  const openAuthorizationForm = () => {
    if (busy) return;
    setBusy(true);
    GetPatientInsurances(patientId, (result) => {
      setBusy(false);
      setPickerOpen(false);
      setAuthorizationForm({ cards: result?.status === 'SUCCESS' ? (result.data?.insurances || []) : [] });
    });
  };

  /**
   * Grava o documento novo e já vincula a este atendimento. Devolve { ok, error }
   * como o hook da ficha; se gravou e só o vínculo falhou, o formulário fecha e o
   * erro aparece no bloco (o documento já está na ficha).
   */
  const createAndLink = (type, create, values) => new Promise((resolve) => {
    create({ patientId, ...values }, (result) => {
      if (result?.status !== 'SUCCESS' || !result.data?.id) {
        resolve({ ok: false, error: result?.error || result?.message || 'Não foi possível concluir. Tente de novo.' });
        return;
      }
      const doc = { id: result.data.id, medical_request_id: values.medical_request_id || null };
      // A recarga sai no onSaved, uma vez, depois dos anexos
      linkWithPairs(type, doc, false).then((failure) => {
        setError(failure ? 'O documento foi registrado na ficha, mas não foi vinculado. Use "Vincular documento".' : null);
        // Pedido vinculado: a pergunta dos próximos atendimentos sai no onSaved (depois dos anexos)
        if (!failure && type === TYPE_REQUEST) upcomingRequestId.current = doc.id;
        resolve({ ok: true, id: doc.id });
      });
    });
  });

  /** Próximos atendimentos sem pedido válido que podem usar o pedido registrado ou renovado */
  const offerUpcoming = (requestId) => {
    GetUpcomingRequestTargets(appointmentId, requestId, (result) => {
      const targets = result?.status === 'SUCCESS' && Array.isArray(result.data) ? result.data : [];
      if (targets.length > 0) setUpcomingOffer({ requestId, targets });
    });
  };

  // Pedido novo ou renovado gravado (e anexos enviados): recarrega uma vez e
  // pergunta dos próximos atendimentos
  const handleRequestSaved = (saved) => {
    refreshAfterChange();
    const requestId = upcomingRequestId.current;
    upcomingRequestId.current = null;
    if (requestId != null && String(requestId) === String(saved?.id)) offerUpcoming(requestId);
  };

  const handleUpcomingConfirmed = () => {
    if (!upcomingOffer || busy) return;
    setBusy(true);
    LinkUpcomingRequest(appointmentId, upcomingOffer.requestId, (result) => {
      setBusy(false);
      setUpcomingOffer(null);
      if (result?.status === 'SUCCESS') {
        if (onChanged) onChanged();
      } else {
        setError(result?.error || 'Não foi possível usar o pedido nos próximos atendimentos.');
      }
    });
  };

  // Lápis do pedido vencido: busca a linha completa na ficha e abre o formulário
  const openEditRequest = (requestId) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    GetPatientInsuranceDocuments(patientId, (result) => {
      setBusy(false);
      const requests = result?.status === 'SUCCESS' && Array.isArray(result.data?.requests) ? result.data.requests : [];
      const request = requests.find((r) => String(r.id) === String(requestId));
      if (request) setEditRequest(request);
      else setError('Não foi possível abrir o pedido. Tente de novo.');
    });
  };

  const saveEditRequest = (values) => new Promise((resolve) => {
    UpdateMedicalRequest({ ...requestPayload(patientId, editRequest), ...values }, (result) => {
      if (result?.status !== 'SUCCESS') {
        resolve({ ok: false, error: result?.error || result?.message || 'Não foi possível salvar o pedido. Tente de novo.' });
        return;
      }
      // Renovado: a recarga e a pergunta dos próximos atendimentos saem no onSaved (depois dos anexos)
      upcomingRequestId.current = editRequest.id;
      resolve({ ok: true, id: editRequest.id });
    });
  });

  const openAttachments = (type, documentId, title) => setAttachments({
    context: type === TYPE_REQUEST ? MEDICAL_REQUEST_CONTEXT : INSURANCE_AUTHORIZATION_CONTEXT,
    contextId: documentId,
    subject: title,
  });

  const handleUnlinkConfirmed = () => {
    if (!unlinkTarget || busy) return;
    setBusy(true);
    UnlinkAppointmentInsuranceDocument(unlinkTarget.id, (result) => {
      setBusy(false);
      setUnlinkTarget(null);
      if (result?.status === 'SUCCESS') {
        loadLinks();
        if (onChanged) onChanged();
      }
    });
  };

  // Mesmo padrão do botão de perfil do painel: a ficha abre em outra aba e a agenda fica
  const openPatientRecord = () => {
    if (patientId) window.open(`/patients/${patientId}?section=insurance`, '_blank');
  };

  if (!appointmentId) return null;

  const registerLink = (
    <div className={styles.pickerActions}>
      <button type="button" className={styles.createBtn} onClick={openRequestForm} disabled={busy}>
        <AddRoundedIcon sx={{ fontSize: 16 }} /> Novo pedido médico
      </button>
      <button type="button" className={styles.createBtn} onClick={openAuthorizationForm} disabled={busy}>
        <AddRoundedIcon sx={{ fontSize: 16 }} /> Nova guia
      </button>
      <button type="button" className={`${styles.linkBtn} ${styles.recordLink}`} onClick={openPatientRecord}>
        Abrir na ficha
      </button>
    </div>
  );

  let pickerContent;
  if (patientDocs === null) {
    pickerContent = <div className={styles.empty}>Carregando...</div>;
  } else if (!hasAnyPatientDoc) {
    pickerContent = (
      <div className={styles.pickerEmpty}>
        <span>Nenhum pedido ou guia cadastrado para esta pessoa.</span>
        {registerLink}
      </div>
    );
  } else if (available.length === 0) {
    pickerContent = (
      <div className={styles.pickerEmpty}>
        <span>
          {unlinked.length > 0
            ? 'Não há pedido nem guia desta operadora para vincular a este atendimento.'
            : 'Os pedidos e guias ativos já estão vinculados a este atendimento.'}
        </span>
        {registerLink}
      </div>
    );
  } else {
    pickerContent = (
      <>
        <ul className={styles.pickerList}>
          {available.map(({ type, doc }) => {
            let usage = null;
            if (type === TYPE_AUTHORIZATION && doc.used_count != null) {
              usage = doc.authorized_quantity
                ? `usada em ${doc.used_count} de ${doc.authorized_quantity}`
                : `usada em ${doc.used_count}`;
            }
            return (
              <li key={`${type}:${doc.id}`}>
                <button
                  type="button"
                  className={styles.pickerItem}
                  onClick={() => handleLink(type, doc)}
                  disabled={busy}
                >
                  <span className={styles.typeTag}>{typeLabel(type)}</span>
                  <div className={styles.itemBody}>
                    <span className={styles.itemTitle}>{documentTitle(type, doc)}</span>
                    <div className={styles.chips}>
                      <StatusChips type={type} data={doc} expired={isExpiredFor(doc)} />
                      {doc.provider_name && <span className={styles.meta}>{doc.provider_name}</span>}
                      {usage && <span className={styles.meta}>{usage}</span>}
                      {type === TYPE_AUTHORIZATION && sessionsUsedUp(doc) && (
                        <span className={styles.meta}>todas as sessões usadas</span>
                      )}
                      {doc.valid_until && <span className={styles.meta}>{dueLabel(doc.valid_until)}</span>}
                      {!doc.valid_until && type === TYPE_REQUEST && (
                        <span className={styles.meta}>Sem vencimento</span>
                      )}
                      {Number(doc.attachments_count) > 0 && (
                        <span className={styles.meta}>{attachmentsLabel(doc.attachments_count)}</span>
                      )}
                      {pairedDocuments(type, doc, patientDocs, linked, providerId).length > 0 && (
                        <span className={styles.meta}>
                          {type === TYPE_REQUEST ? 'leva junto a guia ligada' : 'leva junto o pedido'}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        <div className={styles.pickerFooter}>{registerLink}</div>
      </>
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <span className={styles.label}>Documentos do convênio</span>
        <button type="button" className={styles.linkBtn} onClick={openPicker} disabled={!patientId}>
          <AddLinkOutlinedIcon sx={{ fontSize: 14 }} /> Vincular documento
        </button>
      </div>

      {loading && links.length === 0 && <div className={styles.empty}>Carregando...</div>}
      {!loading && links.length === 0 && (
        <div className={styles.empty}>Nenhum pedido ou guia vinculado a este atendimento.</div>
      )}
      {links.length > 0 && (
        <ul className={styles.list}>
          {links.map(link => {
            const data = link.current || link.snapshot;
            const title = documentTitle(link.document_type, data);
            const expired = Boolean(link.current) && isExpiredFor(link.current);
            const canRenew = expired && link.document_type === TYPE_REQUEST && link.current.active;
            const attachmentsCount = Number(link.attachments_count) || 0;
            // Guia de outra operadora que não a da carteirinha do atendimento: avisa, não bloqueia
            const linkProviderId = link.appointment_provider_id ?? providerId;
            const otherProvider = Boolean(link.current)
              && isOtherProvider(link.document_type, link.current, linkProviderId);
            return (
              <li key={link.id} className={styles.item}>
                <span className={styles.typeTag}>{typeLabel(link.document_type)}</span>
                <div className={styles.itemBody}>
                  <span className={styles.itemTitle} title={title}>{title}</span>
                  <div className={styles.chips}>
                    <StatusChips type={link.document_type} data={link.current} expired={expired} missing={!link.current} />
                    {otherProvider && (
                      <span className={`${styles.chip} ${styles.chipAmber}`}>
                        {link.current.provider_name
                          ? `Guia de outra operadora (${link.current.provider_name})`
                          : 'Guia de outra operadora'}
                      </span>
                    )}
                    {data?.valid_until && <span className={styles.meta}>{dueLabel(data.valid_until)}</span>}
                    {link.current && attachmentsCount > 0 && (
                      <button
                        type="button"
                        className={styles.attachmentsLink}
                        onClick={() => openAttachments(link.document_type, link.document_id, title)}
                      >
                        {attachmentsLabel(attachmentsCount)}
                      </button>
                    )}
                  </div>
                </div>
                {link.current && (
                  <Tooltip title="Anexos">
                    <button
                      type="button"
                      className={`${styles.iconBtn} ${styles.iconBtnEdit}`}
                      onClick={() => openAttachments(link.document_type, link.document_id, title)}
                      aria-label={`Anexos de ${title}`}
                    >
                      <AttachFileOutlinedIcon sx={{ fontSize: 16 }} />
                    </button>
                  </Tooltip>
                )}
                {canRenew && (
                  <Tooltip title="Editar ou renovar o pedido">
                    <button
                      type="button"
                      className={`${styles.iconBtn} ${styles.iconBtnEdit}`}
                      onClick={() => openEditRequest(link.document_id)}
                      disabled={busy}
                      aria-label="Editar ou renovar o pedido"
                    >
                      <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    </button>
                  </Tooltip>
                )}
                <Tooltip title="Desvincular deste atendimento">
                  <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={() => setUnlinkTarget(link)}
                    aria-label={`Desvincular ${title}`}
                  >
                    <LinkOffOutlinedIcon sx={{ fontSize: 16 }} />
                  </button>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      )}
      {error && !pickerOpen && <div className={styles.error}>{error}</div>}

      {/* ── Vincular: pedidos e guias ativos da ficha ── */}
      <ConfirmDialog
        isOpen={pickerOpen}
        title="Vincular documento"
        variant="info"
        onCancel={() => { if (!busy) setPickerOpen(false); }}
        hideFooter
        maxWidth={460}
      >
        {pickerContent}
        {error && <div className={styles.error}>{error}</div>}
      </ConfirmDialog>

      {/* ── Novo pedido / nova guia, já vinculados ao salvar ── */}
      {requestFormOpen && (
        <MedicalRequestFormPopup
          patientId={patientId}
          onSave={(values) => createAndLink(TYPE_REQUEST, SetMedicalRequest, values)}
          onSaved={handleRequestSaved}
          onClose={() => setRequestFormOpen(false)}
        />
      )}
      {editRequest && (
        <MedicalRequestFormPopup
          request={editRequest}
          patientId={patientId}
          onSave={saveEditRequest}
          onSaved={handleRequestSaved}
          onClose={() => setEditRequest(null)}
        />
      )}
      {authorizationForm && (
        <AuthorizationFormPopup
          insurances={authorizationForm.cards}
          providers={providers}
          requests={patientDocs?.requests || []}
          defaultCardId={cardId}
          patientId={patientId}
          onSave={(values) => createAndLink(TYPE_AUTHORIZATION, SetInsuranceAuthorization, values)}
          onSaved={refreshAfterChange}
          onClose={() => setAuthorizationForm(null)}
        />
      )}

      {/* ── Anexos do pedido ou da guia: ver e acrescentar, como na ficha ── */}
      {attachments && (
        <InsuranceDocumentAttachmentsPopup
          patientId={patientId}
          context={attachments.context}
          contextId={attachments.contextId}
          subject={attachments.subject}
          onClose={(changed) => {
            setAttachments(null);
            if (changed) loadLinks(true);
          }}
        />
      )}

      {/* ── Pedido novo ou renovado: usar também nos próximos atendimentos sem pedido válido ── */}
      <ConfirmDialog
        isOpen={Boolean(upcomingOffer)}
        title={upcomingOffer?.targets.length === 1
          ? 'Usar este pedido também no próximo atendimento?'
          : `Usar este pedido também nos próximos ${upcomingOffer?.targets.length || 0} atendimentos?`}
        message={upcomingOffer
          ? `${upcomingOffer.targets.length === 1 ? 'Atendimento de' : 'Atendimentos de'} ${shortDays(upcomingOffer.targets)}, ainda sem pedido válido.`
          : ''}
        confirmLabel="Sim"
        cancelLabel="Não"
        variant="info"
        onConfirm={handleUpcomingConfirmed}
        onCancel={() => { if (!busy) setUpcomingOffer(null); }}
        loading={busy}
      />

      {/* ── Desvincular: confirmação curta ── */}
      <ConfirmDialog
        isOpen={Boolean(unlinkTarget)}
        title="Desvincular documento"
        message="O documento continua na ficha; só deixa de estar ligado a este atendimento."
        confirmLabel="Desvincular"
        variant="warning"
        onConfirm={handleUnlinkConfirmed}
        onCancel={() => { if (!busy) setUnlinkTarget(null); }}
        loading={busy}
      />
    </div>
  );
}
